import { api } from '@/lib/api'
import type {
  Announcement,
  AnnouncementGroups,
  AnnouncementVideoSyncResult,
  AnnouncementWriteInput,
  ArticleCollection,
  ArticleDetail,
  Assignment,
  AudienceOption,
  AudienceWriteInput,
  AuditLogEntry,
  BadgeWriteInput,
  ClientEngagement,
  ClientOrg,
  CreateAssignmentInput,
  HubspotStatus,
  KbAnswer,
  KbStatus,
  LearnerAssignment,
  Leaderboard,
  LearnerBadges,
  LearnerQuiz,
  ManageBadge,
  ManageQuiz,
  ManageQuizQuestion,
  Me,
  QuizAnalytics,
  OrgAnalytics,
  OrgMember,
  Paginated,
  QuizAttemptInput,
  QuizOwnerType,
  QuizQuestionWriteInput,
  QuizVerdict,
  RecordProgressInput,
  SearchResult,
  Section,
  Segment,
  SegmentDetail,
  SegmentTrackMembership,
  Single,
  TeamMember,
  TeamRole,
  Track,
  TrackDetail,
  UserDashboard,
} from '@/api/types'

/** Plural route segment for a quiz owner ('segment' → 'segments'). */
const ownerPath = (ownerType: QuizOwnerType): string => `${ownerType}s`

export interface ListSegmentsParams {
  page?: number
  perPage?: number
  audience?: string
  section?: Section
}

/** Body for track create/update (PATCH ignores omitted fields). */
export interface TrackWriteInput {
  slug?: string
  title?: string
  description?: string
  audience?: string
  section?: Section
  visibility?: string
  position?: number
  estimatedMinutes?: number
}

/** Body for segment create/update. */
export interface SegmentWriteInput {
  slug?: string
  title?: string
  summary?: string
  script?: string | null
  audience?: string
  section?: Section
  visibility?: string
}

export interface PlacementInput {
  slug: string
  required?: boolean
}

export const contentApi = {
  async getMe(): Promise<Me> {
    const { data } = await api.get<Single<Me>>('/api/v1/me')
    return data.data
  },

  async listAudiences(): Promise<AudienceOption[]> {
    const { data } = await api.get<Single<AudienceOption[]>>('/api/v1/audiences')
    return data.data
  },

  async listTracks(section?: Section): Promise<Track[]> {
    const { data } = await api.get<Single<Track[]>>('/api/v1/tracks', { params: { section } })
    return data.data
  },

  async getTrack(slug: string): Promise<TrackDetail> {
    const { data } = await api.get<Single<TrackDetail>>(`/api/v1/tracks/${encodeURIComponent(slug)}`)
    return data.data
  },

  async listSegments(params: ListSegmentsParams = {}): Promise<Paginated<Segment>> {
    const { data } = await api.get<Paginated<Segment>>('/api/v1/segments', { params })
    return data
  },

  async getSegment(slug: string): Promise<SegmentDetail> {
    const { data } = await api.get<Single<SegmentDetail>>(
      `/api/v1/segments/${encodeURIComponent(slug)}`,
    )
    return data.data
  },

  /** Record a viewer progress event (fire-and-forget; 204). */
  async recordProgress(slug: string, input: RecordProgressInput): Promise<void> {
    await api.post(`/api/v1/segments/${encodeURIComponent(slug)}/progress`, input)
  },

  /** The current user's "Your Journey" dashboard (per-track progress + XP). */
  async getUserProgress(): Promise<UserDashboard> {
    const { data } = await api.get<Single<UserDashboard>>('/api/v1/progress/user')
    return data.data
  },

  async search(q: string, section?: Section, audience?: string): Promise<SearchResult[]> {
    const { data } = await api.get<Single<SearchResult[]>>('/api/v1/search', {
      params: { q, section, audience },
    })
    return data.data
  },

  // ---- Knowledgebase (Intercom-synced help articles + AI agent) ----

  /** Browse articles grouped by collection. */
  async listArticles(): Promise<ArticleCollection[]> {
    const { data } = await api.get<Single<{ collections: ArticleCollection[] }>>('/api/v1/articles')
    return data.data.collections
  },

  /** One article (the reader). */
  async getArticle(slug: string): Promise<ArticleDetail> {
    const { data } = await api.get<Single<ArticleDetail>>(
      `/api/v1/articles/${encodeURIComponent(slug)}`,
    )
    return data.data
  },

  /** Ask the KB AI agent a question; returns a grounded answer + cited sources. */
  async askKb(question: string): Promise<KbAnswer> {
    const { data } = await api.post<Single<KbAnswer>>('/api/v1/kb/ask', { question })
    return data.data
  },

  /** KB admin status (content managers only). */
  async getKbStatus(): Promise<KbStatus> {
    const { data } = await api.get<Single<KbStatus>>('/api/v1/kb/status')
    return data.data
  },

  /** Trigger a live Intercom sync now (content managers only). */
  async syncKb(): Promise<{ created: number; updated: number; unpublished: number }> {
    const { data } = await api.post<Single<{ created: number; updated: number; unpublished: number }>>(
      '/api/v1/kb/sync',
    )
    return data.data
  },

  // ---- Badges (learner) ----

  /** The learner's section badge catalog + earned status; also grants newly-met badges. */
  async getBadges(): Promise<LearnerBadges> {
    const { data } = await api.get<Single<LearnerBadges>>('/api/v1/badges')
    return data.data
  },

  // ---- Leaderboard ----

  /** The current user's XP leaderboard (auto-scoped server-side). */
  async getLeaderboard(): Promise<Leaderboard> {
    const { data } = await api.get<Single<Leaderboard>>('/api/v1/leaderboard')
    return data.data
  },

  /** A specific client org's leaderboard (ShopView staff only; 403 otherwise). */
  async getOrgLeaderboard(orgId: string): Promise<Leaderboard> {
    const { data } = await api.get<Single<Leaderboard>>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/leaderboard`,
    )
    return data.data
  },

  /** Client organizations for the staff "pick a client" picker (staff only; 403 otherwise). */
  async listClientOrgs(): Promise<ClientOrg[]> {
    const { data } = await api.get<Single<ClientOrg[]>>('/api/v1/orgs')
    return data.data
  },

  // ---- Analytics ----

  /** Client orgs ranked by engagement (ShopView staff only; 403 otherwise). */
  async listClientsAnalytics(): Promise<ClientEngagement[]> {
    const { data } = await api.get<Single<{ clients: ClientEngagement[] }>>('/api/v1/analytics/clients')
    return data.data.clients
  },

  /** One org's training analytics (staff for any org; customer_admin for their own). */
  async getOrgAnalytics(orgId: string): Promise<OrgAnalytics> {
    const { data } = await api.get<Single<OrgAnalytics>>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/analytics`,
    )
    return data.data
  },

  // ---- Team Assignments (Phase 1B) ----

  /** An org's assignments + per-assignee completion (staff or own-org admin). */
  async listOrgAssignments(orgId: string): Promise<Assignment[]> {
    const { data } = await api.get<Single<{ assignments: Assignment[] }>>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/assignments`,
    )
    return data.data.assignments
  },

  /** An org's active members — the assignee picker (staff or own-org admin). */
  async listOrgMembers(orgId: string): Promise<OrgMember[]> {
    const { data } = await api.get<Single<{ members: OrgMember[] }>>(
      `/api/v1/orgs/${encodeURIComponent(orgId)}/members`,
    )
    return data.data.members
  },

  /** The current learner's own assignments ("My Assignments"). */
  async listUserAssignments(): Promise<LearnerAssignment[]> {
    const { data } = await api.get<Single<{ assignments: LearnerAssignment[] }>>(
      '/api/v1/assignments/user',
    )
    return data.data.assignments
  },

  async createAssignment(input: CreateAssignmentInput): Promise<Assignment> {
    const { data } = await api.post<Single<Assignment>>('/api/v1/assignments', input)
    return data.data
  },

  /** Edit due date / message, or cancel (`{ status: 'cancelled' }`). */
  async updateAssignment(
    id: string,
    input: { dueAt?: string | null; message?: string | null; status?: 'cancelled' },
  ): Promise<Assignment> {
    const { data } = await api.patch<Single<Assignment>>(
      `/api/v1/assignments/${encodeURIComponent(id)}`,
      input,
    )
    return data.data
  },

  /** Manually nudge every not-yet-completed assignee now. */
  async remindAssignment(id: string): Promise<Assignment> {
    const { data } = await api.post<Single<Assignment>>(
      `/api/v1/assignments/${encodeURIComponent(id)}/remind`,
    )
    return data.data
  },

  // ---- HubSpot integration (manager-only; enforced server-side) ----

  /** Integration status for the admin page. */
  async getHubspotStatus(): Promise<HubspotStatus> {
    const { data } = await api.get<Single<HubspotStatus>>('/api/v1/hubspot/status')
    return data.data
  },

  /** Manually re-dispatch one failed sync event. */
  async retryHubspotEvent(id: string): Promise<void> {
    await api.post(`/api/v1/hubspot/events/${encodeURIComponent(id)}/retry`)
  },

  // ---- Content management (manager-only; enforced server-side) ----

  /** Full badge catalog with config (both sections) for the authoring UI. */
  async getManageBadges(): Promise<ManageBadge[]> {
    const { data } = await api.get<Single<ManageBadge[]>>('/api/v1/badges/manage')
    return data.data
  },

  async createBadge(input: BadgeWriteInput): Promise<ManageBadge> {
    const { data } = await api.post<Single<ManageBadge>>('/api/v1/badges', input)
    return data.data
  },

  async updateBadge(key: string, input: BadgeWriteInput): Promise<ManageBadge> {
    const { data } = await api.patch<Single<ManageBadge>>(
      `/api/v1/badges/${encodeURIComponent(key)}`,
      input,
    )
    return data.data
  },

  async deleteBadge(key: string): Promise<void> {
    await api.delete(`/api/v1/badges/${encodeURIComponent(key)}`)
  },

  async createAudience(input: AudienceWriteInput): Promise<AudienceOption> {
    const { data } = await api.post<Single<AudienceOption>>('/api/v1/audiences', input)
    return data.data
  },

  async updateAudience(key: string, input: AudienceWriteInput): Promise<AudienceOption> {
    const { data } = await api.patch<Single<AudienceOption>>(
      `/api/v1/audiences/${encodeURIComponent(key)}`,
      input,
    )
    return data.data
  },

  async deleteAudience(key: string): Promise<void> {
    await api.delete(`/api/v1/audiences/${encodeURIComponent(key)}`)
  },

  async createTrack(input: TrackWriteInput): Promise<Track> {
    const { data } = await api.post<Single<Track>>('/api/v1/tracks', input)
    return data.data
  },

  async updateTrack(slug: string, input: TrackWriteInput): Promise<Track> {
    const { data } = await api.patch<Single<Track>>(`/api/v1/tracks/${encodeURIComponent(slug)}`, input)
    return data.data
  },

  async deleteTrack(slug: string): Promise<void> {
    await api.delete(`/api/v1/tracks/${encodeURIComponent(slug)}`)
  },

  async publishTrack(slug: string, published: boolean): Promise<Track> {
    const { data } = await api.post<Single<Track>>(
      `/api/v1/tracks/${encodeURIComponent(slug)}/publish`,
      { published },
    )
    return data.data
  },

  async setTrackSegments(slug: string, segments: PlacementInput[]): Promise<TrackDetail> {
    const { data } = await api.put<Single<TrackDetail>>(
      `/api/v1/tracks/${encodeURIComponent(slug)}/segments`,
      { segments },
    )
    return data.data
  },

  async createSegment(input: SegmentWriteInput): Promise<Segment> {
    const { data } = await api.post<Single<Segment>>('/api/v1/segments', input)
    return data.data
  },

  async updateSegment(slug: string, input: SegmentWriteInput): Promise<Segment> {
    const { data } = await api.patch<Single<Segment>>(
      `/api/v1/segments/${encodeURIComponent(slug)}`,
      input,
    )
    return data.data
  },

  async deleteSegment(slug: string): Promise<void> {
    await api.delete(`/api/v1/segments/${encodeURIComponent(slug)}`)
  },

  async publishSegment(slug: string, published: boolean): Promise<Segment> {
    const { data } = await api.post<Single<Segment>>(
      `/api/v1/segments/${encodeURIComponent(slug)}/publish`,
      { published },
    )
    return data.data
  },

  /** Tracks in the segment's section, each flagged with membership. */
  async getSegmentTracks(slug: string): Promise<SegmentTrackMembership[]> {
    const { data } = await api.get<Single<SegmentTrackMembership[]>>(
      `/api/v1/segments/${encodeURIComponent(slug)}/tracks`,
    )
    return data.data
  },

  async addSegmentToTrack(trackSlug: string, segmentSlug: string): Promise<void> {
    await api.post(
      `/api/v1/tracks/${encodeURIComponent(trackSlug)}/segments/${encodeURIComponent(segmentSlug)}`,
    )
  },

  async removeSegmentFromTrack(trackSlug: string, segmentSlug: string): Promise<void> {
    await api.delete(
      `/api/v1/tracks/${encodeURIComponent(trackSlug)}/segments/${encodeURIComponent(segmentSlug)}`,
    )
  },

  /** Ask the backend for a one-time Mux direct-upload URL for this segment. */
  async requestSegmentUpload(slug: string): Promise<MuxUpload> {
    const { data } = await api.post<Single<MuxUpload>>(
      `/api/v1/segments/${encodeURIComponent(slug)}/upload`,
    )
    return data.data
  },

  // ---- What's New (announcements) ----

  /** Browse What's New — the upcoming + released buckets the caller may see. */
  async listAnnouncements(): Promise<AnnouncementGroups> {
    const { data } = await api.get<Single<AnnouncementGroups>>('/api/v1/announcements')
    return data.data
  },

  /** A single entry (incl. video status + a freshly-minted playback token). */
  async getAnnouncement(slug: string): Promise<Announcement> {
    const { data } = await api.get<Single<Announcement>>(
      `/api/v1/announcements/${encodeURIComponent(slug)}`,
    )
    return data.data
  },

  async createAnnouncement(input: AnnouncementWriteInput): Promise<Announcement> {
    const { data } = await api.post<Single<Announcement>>('/api/v1/announcements', input)
    return data.data
  },

  async updateAnnouncement(slug: string, input: AnnouncementWriteInput): Promise<Announcement> {
    const { data } = await api.patch<Single<Announcement>>(
      `/api/v1/announcements/${encodeURIComponent(slug)}`,
      input,
    )
    return data.data
  },

  async publishAnnouncement(slug: string, published: boolean): Promise<Announcement> {
    const { data } = await api.post<Single<Announcement>>(
      `/api/v1/announcements/${encodeURIComponent(slug)}/publish`,
      { published },
    )
    return data.data
  },

  async deleteAnnouncement(slug: string): Promise<void> {
    await api.delete(`/api/v1/announcements/${encodeURIComponent(slug)}`)
  },

  /** One-time Mux direct-upload URL for an entry's video (content managers only). */
  async requestAnnouncementUpload(slug: string): Promise<MuxUpload> {
    const { data } = await api.post<Single<MuxUpload>>(
      `/api/v1/announcements/${encodeURIComponent(slug)}/upload`,
    )
    return data.data
  },

  /** Reconcile an entry's video from Mux when the webhook didn't arrive. */
  async syncAnnouncementVideo(slug: string): Promise<AnnouncementVideoSyncResult> {
    const { data } = await api.post<Single<AnnouncementVideoSyncResult>>(
      `/api/v1/announcements/${encodeURIComponent(slug)}/sync-video`,
    )
    return data.data
  },

  // ---- Quizzes (learner) ----

  /** The learner-facing quiz for a segment/track (answers hidden) + the user's status. */
  async getQuiz(ownerType: QuizOwnerType, slug: string): Promise<LearnerQuiz> {
    const { data } = await api.get<Single<LearnerQuiz>>(
      `/api/v1/${ownerPath(ownerType)}/${encodeURIComponent(slug)}/quiz`,
    )
    return data.data
  },

  /** Submit one answer; returns the verdict (answer revealed) + refreshed status. */
  async submitQuizAttempt(
    ownerType: QuizOwnerType,
    slug: string,
    input: QuizAttemptInput,
  ): Promise<QuizVerdict> {
    const { data } = await api.post<Single<QuizVerdict>>(
      `/api/v1/${ownerPath(ownerType)}/${encodeURIComponent(slug)}/quiz/attempts`,
      input,
    )
    return data.data
  },

  // ---- Quizzes (authoring; manager-only, enforced server-side) ----

  /** Author view of a quiz — questions WITH the grading key. */
  async getManageQuiz(ownerType: QuizOwnerType, slug: string): Promise<ManageQuiz> {
    const { data } = await api.get<Single<ManageQuiz>>(
      `/api/v1/${ownerPath(ownerType)}/${encodeURIComponent(slug)}/quiz/questions`,
    )
    return data.data
  },

  /** Per-question analytics for a quiz (manager-only). */
  async getQuizAnalytics(ownerType: QuizOwnerType, slug: string): Promise<QuizAnalytics> {
    const { data } = await api.get<Single<QuizAnalytics>>(
      `/api/v1/${ownerPath(ownerType)}/${encodeURIComponent(slug)}/quiz/analytics`,
    )
    return data.data
  },

  async createQuizQuestion(
    ownerType: QuizOwnerType,
    slug: string,
    input: QuizQuestionWriteInput,
  ): Promise<ManageQuizQuestion> {
    const { data } = await api.post<Single<ManageQuizQuestion>>(
      `/api/v1/${ownerPath(ownerType)}/${encodeURIComponent(slug)}/quiz/questions`,
      input,
    )
    return data.data
  },

  async updateQuizQuestion(
    questionId: string,
    input: QuizQuestionWriteInput,
  ): Promise<ManageQuizQuestion> {
    const { data } = await api.patch<Single<ManageQuizQuestion>>(
      `/api/v1/quiz-questions/${encodeURIComponent(questionId)}`,
      input,
    )
    return data.data
  },

  async deleteQuizQuestion(questionId: string): Promise<void> {
    await api.delete(`/api/v1/quiz-questions/${encodeURIComponent(questionId)}`)
  },

  // ---- Team management + audit log (Owner only) ----

  /** Internal staff with their access levels (Owners → Admins → Users). */
  async listTeamMembers(): Promise<TeamMember[]> {
    const { data } = await api.get<Single<{ members: TeamMember[] }>>('/api/v1/team/members')
    return data.data.members
  },

  /** Set an internal teammate's access level. The backend rejects self-lockout + external roles. */
  async changeMemberRole(userId: string, role: TeamRole): Promise<TeamMember> {
    const { data } = await api.patch<Single<TeamMember>>(
      `/api/v1/team/members/${encodeURIComponent(userId)}/role`,
      { role },
    )
    return data.data
  },

  /** A page of audit entries (newest first). Returns the full pagination envelope. */
  async listAuditLog(page = 1, perPage?: number): Promise<Paginated<AuditLogEntry>> {
    const { data } = await api.get<Paginated<AuditLogEntry>>('/api/v1/audit-log', {
      params: { page, ...(undefined !== perPage ? { perPage } : {}) },
    })
    return data
  },
}

export interface MuxUpload {
  uploadUrl: string
  uploadId: string
}
